export interface DateRangeValue {
  from: Date | null;
  to: Date | null;
}

export interface BlockedDateRange extends DateRangeValue {
  id?: string;
  label?: string;
}

export interface DateRangeOptions {
  withTime?: boolean;
  allowOpenRange?: boolean;
  required?: boolean;
  blockedRanges?: readonly BlockedDateRange[];
}

export type DateRangePosition = 'from' | 'to';
export type DateRangeErrors = Partial<Record<DateRangePosition, string>>;

export const isValidRangeDate = (date: Date | null): date is Date =>
  date instanceof Date && Number.isFinite(date.getTime());

const startOfDay = (date: Date) => {
  const result = new Date(date);
  result.setHours(0, 0, 0, 0);
  return result;
};

const nextDay = (date: Date) => {
  const result = startOfDay(date);
  result.setDate(result.getDate() + 1);
  return result;
};

// Date-only ranges include the last day. Date-time ranges exclude the end
// instant, so consecutive periods can share a boundary. Null means unbounded.
const bounds = ({ from, to }: DateRangeValue, withTime: boolean) => ({
  from: from ? (withTime ? from : startOfDay(from)).getTime() : -Infinity,
  to: to ? (withTime ? to : nextDay(to)).getTime() : Infinity,
});

export const getDateRangeConflicts = (
  value: DateRangeValue,
  blockedRanges: readonly BlockedDateRange[],
  withTime = false
) => {
  if (
    (value.from !== null && !isValidRangeDate(value.from)) ||
    (value.to !== null && !isValidRangeDate(value.to))
  )
    return [];
  const selected = bounds(value, withTime);
  if (selected.from >= selected.to) return [];
  return blockedRanges.filter((range) => {
    const occupied = bounds(range, withTime);
    return selected.from < occupied.to && selected.to > occupied.from;
  });
};

export const getDateRangeErrors = (
  value: DateRangeValue,
  {
    withTime = false,
    allowOpenRange = false,
    required = false,
    blockedRanges = [],
  }: DateRangeOptions = {}
): DateRangeErrors => {
  const errors: DateRangeErrors = {};
  for (const position of ['from', 'to'] as const) {
    const date = value[position];
    if (date !== null && !isValidRangeDate(date)) {
      errors[position] = 'Ingresá una fecha válida';
    } else if (
      date &&
      (date.getFullYear() < 1900 || date.getFullYear() > 2099)
    ) {
      errors[position] = 'Elegí una fecha entre 1900 y 2099';
    } else if (
      date === null &&
      (required ||
        (!allowOpenRange &&
          value[position === 'from' ? 'to' : 'from'] !== null))
    ) {
      errors[position] =
        position === 'from'
          ? 'Ingresá la fecha de inicio'
          : 'Ingresá la fecha de fin';
    }
  }
  if (Object.keys(errors).length) return errors;
  // An empty optional date filter has no selection. An open validity does.
  if (!allowOpenRange && !value.from && !value.to) return errors;
  const selected = bounds(value, withTime);
  if (selected.from >= selected.to) {
    errors.to = withTime
      ? 'El fin debe ser posterior al inicio'
      : 'El fin no puede ser anterior al inicio';
    return errors;
  }
  if (getDateRangeConflicts(value, blockedRanges, withTime).length) {
    errors.to =
      'El período coincide con fechas ocupadas. Ajustá el inicio o el fin.';
  }
  return errors;
};

/** Find the earliest selectable instant in this day/hour/minute/second.
 * Checking entire units keeps partially available days and hours selectable.
 * The start remains editable even if the previously selected end is invalid.
 */
const firstAvailableInstant = (
  value: Date,
  position: DateRangePosition,
  unit: 'day' | 'hours' | 'minutes' | 'seconds',
  from: Date | null,
  blockedRanges: readonly BlockedDateRange[],
  withTime = false
) => {
  if (!isValidRangeDate(value)) return null;
  const lowDate = new Date(value);
  const highDate = new Date(value);
  if (!withTime || unit === 'day') {
    lowDate.setHours(0, 0, 0, 0);
    highDate.setHours(0, 0, 0, 0);
    highDate.setDate(highDate.getDate() + 1);
  } else if (unit === 'hours') {
    lowDate.setMinutes(0, 0, 0);
    highDate.setMinutes(60, 0, 0);
  } else if (unit === 'minutes') {
    lowDate.setSeconds(0, 0);
    highDate.setSeconds(60, 0);
  } else {
    lowDate.setMilliseconds(0);
    highDate.setMilliseconds(1000);
  }
  let low = lowDate.getTime();
  let high = withTime ? highDate.getTime() - 1 : low;
  const occupied = blockedRanges
    .map((range) => bounds(range, withTime))
    .sort((a, b) => a.from - b.from);

  if (position === 'to' && isValidRangeDate(from)) {
    const start = (withTime ? from : startOfDay(from)).getTime();
    low = Math.max(low, start + (withTime ? 1 : 0));
    for (const range of occupied) {
      if (range.from <= start && start < range.to) return null;
      if (range.from > start) {
        high = Math.min(high, range.from - (withTime ? 0 : 1));
      }
    }
  }
  // Inputs expose seconds, so do not offer an hour that only has free milliseconds.
  let candidate = withTime ? Math.ceil(low / 1000) * 1000 : low;
  for (const range of occupied) {
    const endPosition = withTime && position === 'to';
    const isOccupied = endPosition
      ? candidate > range.from && candidate <= range.to
      : candidate >= range.from && candidate < range.to;
    if (isOccupied) {
      candidate = endPosition
        ? Math.floor(range.to / 1000) * 1000 + 1000
        : withTime
          ? Math.ceil(range.to / 1000) * 1000
          : range.to;
    }
  }
  return candidate > high || !Number.isFinite(candidate) ? null : candidate;
};

export const isDateRangeUnitDisabled = (
  ...args: Parameters<typeof firstAvailableInstant>
) => firstAvailableInstant(...args) === null;

/** Keep the selected time when possible; otherwise use a free time on this day.
 * Partially occupied days must never turn into an occupied midnight by default.
 */
export const getDateRangeDateForDay = (
  day: Date,
  position: DateRangePosition,
  value: DateRangeValue,
  blockedRanges: readonly BlockedDateRange[] = [],
  withTime = false
): Date | null => {
  const candidate = startOfDay(day);
  const previous = value[position];
  if (withTime && isValidRangeDate(previous)) {
    candidate.setHours(
      previous.getHours(),
      previous.getMinutes(),
      previous.getSeconds()
    );
  } else if (
    withTime &&
    position === 'to' &&
    isValidRangeDate(value.from) &&
    startOfDay(value.from).getTime() === candidate.getTime()
  ) {
    candidate.setHours(
      value.from.getHours(),
      value.from.getMinutes() + 60,
      value.from.getSeconds()
    );
  }
  if (
    startOfDay(candidate).getTime() === startOfDay(day).getTime() &&
    !isDateRangeUnitDisabled(
      candidate,
      position,
      'seconds',
      value.from,
      blockedRanges,
      withTime
    )
  )
    return candidate;
  const instant = firstAvailableInstant(
    day,
    position,
    'day',
    value.from,
    blockedRanges,
    withTime
  );
  return instant === null ? null : new Date(instant);
};
